import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDatabase } from '../contexts/DatabaseContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

const AdminPage = () => {
  const { user, isAdmin } = useAuth();
  const { fetchData, updateDocument } = useDatabase();
  const { theme, themes } = useTheme();
  const navigate = useNavigate();
  const [pendingFacts, setPendingFacts] = useState([]);
  const [approvedFacts, setApprovedFacts] = useState([]);
  const [deniedFacts, setDeniedFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [editingFact, setEditingFact] = useState(null);
  const [editForm, setEditForm] = useState({ fact: '', category: '' });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    if (!isAdmin()) {
      navigate('/');
      return;
    }

    loadPendingFacts();
    loadApprovedFacts();
    loadDeniedFacts();
  }, [user, isAdmin, navigate]);

  const loadPendingFacts = async () => {
    try {
      setLoading(true);
      console.log('🔍 Admin loading pending facts...');
      console.log('🔍 User info:', user);
      console.log('🔍 Is admin check:', isAdmin());
      const facts = await fetchData('funFacts', { where: { field: 'status', operator: '==', value: 'pending' } });
      setPendingFacts(facts);
    } catch (err) {
      console.error('❌ Failed to load pending fun facts:', err);
      setError('Failed to load pending fun facts');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedFacts = async () => {
    try {
      setLoading(true);
      console.log('🔍 Admin loading approved facts...');
      const facts = await fetchData('funFacts', { where: { field: 'status', operator: '==', value: 'approved' } });
      setApprovedFacts(facts);
    } catch (err) {
      console.error('❌ Failed to load approved fun facts:', err);
      setError('Failed to load approved fun facts');
    } finally {
      setLoading(false);
    }
  };

  const loadDeniedFacts = async () => {
    try {
      setLoading(true);
      console.log('🔍 Admin loading denied facts...');
      const facts = await fetchData('funFacts', { where: { field: 'status', operator: '==', value: 'denied' } });
      setDeniedFacts(facts);
    } catch (err) {
      console.error('❌ Failed to load denied fun facts:', err);
      setError('Failed to load denied fun facts');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (factId) => {
    try {
      await updateDocument('funFacts', factId, { status: 'approved', approvedAt: new Date().toISOString() });
      setPendingFacts(prev => prev.filter(fact => fact.id !== factId));
    } catch (err) {
      console.error('Failed to approve fun fact:', err);
      alert('Failed to approve fun fact');
    }
  };

  const handleDeny = async (factId, currentStatus = 'pending') => {
    try {
      await updateDocument('funFacts', factId, { status: 'denied', deniedAt: new Date().toISOString() });
      if (currentStatus === 'pending') {
        setPendingFacts(prev => prev.filter(fact => fact.id !== factId));
      } else if (currentStatus === 'approved') {
        setApprovedFacts(prev => prev.filter(fact => fact.id !== factId));
      }
      // Reload denied facts to show the newly denied item
      loadDeniedFacts();
    } catch (err) {
      console.error('Failed to deny fun fact:', err);
      alert('Failed to deny fun fact');
    }
  };

  const handleEdit = (fact) => {
    setEditingFact(fact);
    setEditForm({ fact: fact.fact, category: fact.category || '' });
  };

  const handleSaveEdit = async () => {
    try {
      await updateDocument('funFacts', editingFact.id, {
        fact: editForm.fact,
        category: editForm.category,
        updatedAt: new Date().toISOString()
      });
      
      // Update the fact in the appropriate list
      const updateFactInList = (list, setList) => {
        setList(prev => prev.map(f => 
          f.id === editingFact.id 
            ? { ...f, fact: editForm.fact, category: editForm.category }
            : f
        ));
      };

      if (editingFact.status === 'pending') {
        updateFactInList(pendingFacts, setPendingFacts);
      } else if (editingFact.status === 'approved') {
        updateFactInList(approvedFacts, setApprovedFacts);
      } else if (editingFact.status === 'denied') {
        updateFactInList(deniedFacts, setDeniedFacts);
      }

      setEditingFact(null);
      setEditForm({ fact: '', category: '' });
    } catch (err) {
      console.error('Failed to update fun fact:', err);
      alert('Failed to update fun fact');
    }
  };

  const handleCancelEdit = () => {
    setEditingFact(null);
    setEditForm({ fact: '', category: '' });
  };

  if (loading) {
    const currentTheme = themes[theme];
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: currentTheme.bgSecondary }}>
        <div style={{ color: currentTheme.textSecondary }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    const currentTheme = themes[theme];
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: currentTheme.bgSecondary }}>
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  const currentTheme = themes[theme];
  
  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: currentTheme.bgSecondary }}>
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8" style={{ color: currentTheme.textPrimary }}>Admin Dashboard</h1>
        
        <div className="rounded-lg shadow-md p-6 mb-6" style={{ backgroundColor: currentTheme.bgPrimary }}>
          {/* Tab Navigation */}
          <div className="mb-6" style={{ borderBottom: `1px solid ${currentTheme.border}` }}>
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'pending'
                    ? ''
                    : 'border-transparent'
                }`}
                style={{
                  borderBottomColor: activeTab === 'pending' ? currentTheme.accent : 'transparent',
                  color: activeTab === 'pending' ? currentTheme.accent : currentTheme.textSecondary
                }}
              >
                Pending ({pendingFacts.length})
              </button>
              <button
                onClick={() => setActiveTab('approved')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'approved'
                    ? ''
                    : 'border-transparent'
                }`}
                style={{
                  borderBottomColor: activeTab === 'approved' ? currentTheme.accent : 'transparent',
                  color: activeTab === 'approved' ? currentTheme.accent : currentTheme.textSecondary
                }}
              >
                Approved ({approvedFacts.length})
              </button>
              <button
                onClick={() => setActiveTab('denied')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'denied'
                    ? ''
                    : 'border-transparent'
                }`}
                style={{
                  borderBottomColor: activeTab === 'denied' ? currentTheme.accent : 'transparent',
                  color: activeTab === 'denied' ? currentTheme.accent : currentTheme.textSecondary
                }}
              >
                Denied ({deniedFacts.length})
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'pending' && (
            <div>
              <h2 className="text-xl font-semibold mb-4" style={{ color: currentTheme.textPrimary }}>Pending Fun Facts</h2>
              
              {pendingFacts.length === 0 ? (
                <p style={{ color: currentTheme.textTertiary }}>No pending fun facts to review.</p>
              ) : (
                <div className="space-y-4">
                  {pendingFacts.map(fact => (
                    <div key={fact.id} className="rounded-lg p-4 hover:shadow-md transition-shadow" style={{ border: `1px solid ${currentTheme.border}` }}>
                      <div className="mb-3">
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded" style={{ backgroundColor: currentTheme.bgTertiary, color: currentTheme.accent }}>
                          {fact.category || 'Uncategorized'}
                        </span>
                        <span className="ml-2 text-sm" style={{ color: currentTheme.textTertiary }}>
                          Submitted by: {fact.submittedBy || 'Anonymous'}
                        </span>
                      </div>
                      
                      <p className="mb-4" style={{ color: currentTheme.textPrimary }}>{fact.fact}</p>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEdit(fact)}
                          className="px-4 py-2 rounded-md transition-colors hover:opacity-80"
                          style={{ backgroundColor: currentTheme.accent, color: currentTheme.accentContrast }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleApprove(fact.id)}
                          className="px-4 py-2 rounded-md transition-colors"
                          style={{ backgroundColor: '#10b981', color: 'white' }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleDeny(fact.id, 'pending')}
                          className="px-4 py-2 rounded-md transition-colors"
                          style={{ backgroundColor: '#ef4444', color: 'white' }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'approved' && (
            <div>
              <h2 className="text-xl font-semibold mb-4" style={{ color: currentTheme.textPrimary }}>Approved Fun Facts</h2>
              
              {approvedFacts.length === 0 ? (
                <p style={{ color: currentTheme.textTertiary }}>No approved fun facts.</p>
              ) : (
                <div className="space-y-4">
                  {approvedFacts.map(fact => (
                    <div key={fact.id} className="rounded-lg p-4 hover:shadow-md transition-shadow" style={{ border: `1px solid ${currentTheme.border}` }}>
                      <div className="mb-3">
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                          {fact.category || 'Uncategorized'}
                        </span>
                        <span className="ml-2 text-sm" style={{ color: currentTheme.textTertiary }}>
                          Submitted by: {fact.submittedBy || 'Anonymous'}
                        </span>
                      </div>
                      
                      <p className="mb-4" style={{ color: currentTheme.textPrimary }}>{fact.fact}</p>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEdit(fact)}
                          className="px-4 py-2 rounded-md transition-colors hover:opacity-80"
                          style={{ backgroundColor: currentTheme.accent, color: currentTheme.accentContrast }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeny(fact.id, 'approved')}
                          className="px-4 py-2 rounded-md transition-colors"
                          style={{ backgroundColor: '#ef4444', color: 'white' }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                        >
                          Deny (Remove)
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'denied' && (
            <div>
              <h2 className="text-xl font-semibold mb-4" style={{ color: currentTheme.textPrimary }}>Denied Fun Facts</h2>
              
              {deniedFacts.length === 0 ? (
                <p style={{ color: currentTheme.textTertiary }}>No denied fun facts.</p>
              ) : (
                <div className="space-y-4">
                  {deniedFacts.map(fact => (
                    <div key={fact.id} className="rounded-lg p-4 hover:shadow-md transition-shadow" style={{ border: `1px solid ${currentTheme.border}` }}>
                      <div className="mb-3">
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
                          {fact.category || 'Uncategorized'}
                        </span>
                        <span className="ml-2 text-sm" style={{ color: currentTheme.textTertiary }}>
                          Submitted by: {fact.submittedBy || 'Anonymous'}
                        </span>
                      </div>
                      
                      <p className="mb-4" style={{ color: currentTheme.textPrimary }}>{fact.fact}</p>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEdit(fact)}
                          className="px-4 py-2 rounded-md transition-colors hover:opacity-80"
                          style={{ backgroundColor: currentTheme.accent, color: currentTheme.accentContrast }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleApprove(fact.id)}
                          className="px-4 py-2 rounded-md transition-colors"
                          style={{ backgroundColor: '#10b981', color: 'white' }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          Approve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="rounded-lg shadow-md p-6" style={{ backgroundColor: currentTheme.bgPrimary }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: currentTheme.textPrimary }}>Quick Stats</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: currentTheme.accent }}>{pendingFacts.length}</div>
              <div className="text-sm" style={{ color: currentTheme.textSecondary }}>Pending</div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingFact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="rounded-lg p-6 max-w-lg w-full" style={{ backgroundColor: currentTheme.bgPrimary }}>
            <h3 className="text-xl font-semibold mb-4" style={{ color: currentTheme.textPrimary }}>
              Edit Fun Fact
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.textSecondary }}>
                Category
              </label>
              <input
                type="text"
                value={editForm.category}
                onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: currentTheme.bgSecondary,
                  borderColor: currentTheme.border,
                  color: currentTheme.textPrimary,
                  focusRingColor: currentTheme.accent
                }}
                placeholder="Enter category (optional)"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.textSecondary }}>
                Fun Fact
              </label>
              <textarea
                value={editForm.fact}
                onChange={(e) => setEditForm(prev => ({ ...prev, fact: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 resize-none"
                style={{ 
                  backgroundColor: currentTheme.bgSecondary,
                  borderColor: currentTheme.border,
                  color: currentTheme.textPrimary,
                  focusRingColor: currentTheme.accent,
                  minHeight: '100px'
                }}
                placeholder="Enter the fun fact"
                rows={4}
              />
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 rounded-md transition-colors"
                style={{ 
                  backgroundColor: currentTheme.bgTertiary, 
                  color: currentTheme.textSecondary 
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 rounded-md transition-colors"
                style={{ backgroundColor: currentTheme.accent, color: currentTheme.accentContrast }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
